import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import ClientLayout from '../../components/layout/ClientLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Send, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientMessagesPage() {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/client/messages`);
      setMessages(res.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
    const t = setInterval(fetchMessages, 15000);
    return () => clearInterval(t);
  }, [fetchMessages]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      await axios.post(`${API}/client/messages`, { text: body });
      setText('');
      await fetchMessages();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <ClientLayout>
      <div className="space-y-4" data-testid="client-messages-page">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold text-zinc-900 tracking-tight">Messages</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Chat directly with the S.A.N Security team.</p>
        </div>

        <Card className="bg-white border-zinc-200 rounded-lg shadow-sm">
          <CardContent className="p-0 flex flex-col" style={{ height: '60vh' }}>
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="client-messages-list">
              {loading ? (
                <p className="text-center text-sm text-zinc-500 py-8">Loading…</p>
              ) : messages.length === 0 ? (
                <div className="text-center py-10 space-y-2">
                  <MessageSquare className="w-10 h-10 mx-auto text-zinc-300" />
                  <p className="text-sm text-zinc-500">No messages yet — say hello!</p>
                </div>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender_role === 'client' ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.id}`}>
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 ${
                      m.sender_role === 'client' ? 'bg-[#14181f] text-white' : 'bg-zinc-100 text-zinc-900'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                      <p className={`text-[10px] mt-1 ${m.sender_role === 'client' ? 'text-white/50' : 'text-zinc-400'}`}>
                        {m.sender_role === 'admin' ? `${m.sender_name} · ` : ''}
                        {(() => { try { return format(parseISO(m.created_at), 'dd MMM HH:mm'); } catch { return ''; } })()}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <div ref={bottomRef} />
            </div>
            <div className="border-t border-zinc-200 p-3 flex gap-2 items-end">
              <Textarea
                rows={1}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Type a message…"
                className="rounded-md resize-none min-h-[42px]"
                data-testid="client-msg-input"
              />
              <Button
                onClick={send}
                disabled={sending || !text.trim()}
                className="h-10 bg-[#14181f] text-white hover:bg-[#232a35] rounded-md shrink-0"
                data-testid="client-msg-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </ClientLayout>
  );
}
